import {
  CalendarOutlined,
  CommentOutlined,
  LoadingOutlined,
  LockOutlined,
  PhoneOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import useAuth from '@hooks/use-auth';
import useLastCallback from '@hooks/use-last-callback';
import useSessionStorage from '@hooks/use-session-storage';
import type { Conversation } from '@interfaces/conversation';
import type { TelegramUser } from '@interfaces/telegram-user';
import { App, Button, DatePicker, Dropdown, type MenuProps, Tooltip } from 'antd';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useActiveConversation, useActiveConversationMembers, useSharedActions } from '@/newstore/selectors/shared';

import styles from './MiddleHeader.module.scss';

export const SHOW_TELEGRAM_MENU_LABELS_KEY = 'show-telegram-menu-labels';

interface StateProps {
  conversation: Conversation;
  conversationUsers: TelegramUser[];
}

function getClientTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

const NonMemoMiddleHeader: FC<StateProps> = ({ conversation, conversationUsers }) => {
  const auth = useAuth();
  const { message } = App.useApp();
  const { toggleChatInfo, openMiddleSearch, loadMessagesByDate } = useSharedActions();
  const [showTelegramMenuLabels, setShowTelegramMenuLabels] = useSessionStorage(SHOW_TELEGRAM_MENU_LABELS_KEY, false);
  const [isDateLoading, setIsDateLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs>();
  const requestIdRef = useRef(0);

  const clientTimeZone = useMemo(getClientTimeZone, []);
  const selectedDateLabel = selectedDate?.format('MMM D, YYYY');

  const callableUsers = useMemo(
    () => conversationUsers.filter((user) => user.phone_number !== null),
    [conversationUsers],
  );

  useEffect(() => {
    requestIdRef.current += 1;
    setIsDateLoading(false);
    setSelectedDate(undefined);
  }, [conversation.id]);

  const toggleTelegramMenuLabels = useCallback(() => {
    setShowTelegramMenuLabels((value) => !value);
  }, [setShowTelegramMenuLabels]);

  const items: MenuProps['items'] = useMemo(
    () =>
      callableUsers.map((user) => ({
        key: user.id,
        label: (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`tel:${user.phone_number}`}
            title={`Call ${user.full_name}`}
          >
            {user.full_name}
          </a>
        ),
      })),
    [callableUsers],
  );

  const handleSearchClick = useLastCallback(() => {
    openMiddleSearch();
  });

  const handleDateSelect = useLastCallback(async (date: Dayjs) => {
    const selectedDate = date.format('YYYY-MM-DD');
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setIsDateLoading(true);

    try {
      await loadMessagesByDate(auth, conversation.id, selectedDate, clientTimeZone);
      if (requestIdRef.current === requestId) {
        setSelectedDate(date);
      }
    } catch (error) {
      console.error('Failed to load messages by date:', error);
      message.error('Failed to load messages for the selected date.');
    } finally {
      if (requestIdRef.current === requestId) {
        setIsDateLoading(false);
      }
    }
  });

  return (
    <div className={styles.chatHeader}>
      <div className={styles.chatTitleWrapper}>
        <Button className={styles.chatTitleButton} color="default" variant="link" onClick={() => toggleChatInfo()}>
          <h1 className={styles.chatTitle}>{conversation.name}</h1>
        </Button>
        {!conversation.is_authorized && <LockOutlined className={styles.chatLock} />}
        <Tooltip
          title={
            showTelegramMenuLabels
              ? 'Show web service messages for Telegram menu requests'
              : 'Show Telegram style messages for Telegram menu requests'
          }
        >
          <Button
            className={styles.telegramMenuLabelsButton}
            size="small"
            type={showTelegramMenuLabels ? 'primary' : 'text'}
            icon={<CommentOutlined />}
            aria-label={
              showTelegramMenuLabels
                ? 'Show web service messages for Telegram menu requests'
                : 'Show Telegram menu labels for menu requests'
            }
            aria-pressed={showTelegramMenuLabels}
            onClick={toggleTelegramMenuLabels}
          />
        </Tooltip>
      </div>
      <div className={styles.headerActions}>
        <Tooltip
          title={
            selectedDateLabel
              ? `Jumped to ${selectedDateLabel} (${clientTimeZone})`
              : `Jump to date (${clientTimeZone})`
          }
        >
          <DatePicker
            className={styles.messageDatePicker}
            value={null}
            defaultPickerValue={selectedDate}
            allowClear={false}
            inputReadOnly
            disabled={isDateLoading}
            suffixIcon={isDateLoading ? <LoadingOutlined /> : <CalendarOutlined />}
            aria-label="Jump to conversation messages by date"
            onChange={(date) => {
              if (date) {
                void handleDateSelect(date);
              }
            }}
          />
        </Tooltip>
        <Button
          onClick={handleSearchClick}
          icon={<SearchOutlined />}
          aria-label="Search the conversation"
          title="Search the conversation..."
        />
        {callableUsers.length > 1 && (
          <Dropdown menu={{ items }}>
            <Button>
              Call <PhoneOutlined />
            </Button>
          </Dropdown>
        )}
        {callableUsers.length === 1 && (
          <Button href={`tel:${callableUsers[0].phone_number}`}>
            <PhoneOutlined /> Call {callableUsers[0].full_name}
          </Button>
        )}
      </div>
    </div>
  );
};

const MiddleHeader = memo(function MiddleHeader() {
  const { conversation, isLoading } = useActiveConversation();
  const conversationUsers = useActiveConversationMembers();

  if (isLoading || !conversation) {
    return null;
  }

  return <NonMemoMiddleHeader conversation={conversation} conversationUsers={conversationUsers ?? []} />;
});

export default MiddleHeader;
